import React, { FC } from 'react'

import { WrappedFieldProps } from 'redux-form'

import { Box } from '@material-ui/core'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import {
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps
} from '@material-ui/lab'

import FormControl, { IFormControl } from '~/components/shared/FormControl'

import useOptions, { OptionsProps } from '~/hooks/useOptions'

import { Theme } from '~/theme/theme'

import { IFieldWithOptions } from '~/interfaces/shared'

import ExtendedButton from '../shared/ExtendedButton'
import Loader from '../shared/Loader'

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      label: {
        textAlign: (props: { center: boolean }) =>
          props.center ? 'center' : 'left'
      },
      buttonGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: (props: { center: boolean }) =>
          props.center ? 'center' : 'flex-start',
        margin: -theme.spacing(0.6)
      },
      grouped: {
        fontSize: 16,
        '&.MuiToggleButton-sizeLarge': {
          padding: '14px 20px',
          [theme.breakpoints.down('sm')]: {
            padding: '7px 10px'
          }
        },
        '&:not(.MuiToggleButton-sizeSmall):not(.MuiToggleButton-sizeLarge)': {
          padding: '6px 20px'
        },
        color: `${theme.palette.text.primary} !important`,
        margin: `${theme.spacing(0.6)}px !important`,
        border: `1px solid ${theme.palette.divider} !important`,
        borderRadius: `${theme.shape.borderRadius}px !important`,
        background: theme.palette.common.white,
        textTransform: 'none',
        '&:hover': {
          border: `1px solid ${theme.palette.divider} !important`,
          background: theme.palette.common.white
        }
      },
      selected: {
        '&, &:hover, &:focus': {
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.primary.main} !important`,
          background: `${theme.palette.common.white} !important`
        }
      }
    }),
  { name: 'ToggleButtonField' }
)

const placeholderOptions = new Array(5).fill(null)

interface Props
  extends IFieldWithOptions,
    Omit<IFormControl, 'size'>,
    WrappedFieldProps {
  center?: boolean
  multiple?: boolean
  enforceValue?: boolean
  selectAll?: boolean
  processing?: boolean
  size?: ToggleButtonProps['size']
  optionSettings?: Partial<OptionsProps>
}

const ToggleButtonField: FC<Props> = ({
  options: customOptions = [],
  input: { onChange, value, name },
  meta: { touched, error },
  enforceValue,
  label,
  center,
  helperText,
  multiple,
  selectAll,
  processing,
  required,
  size = 'large',
  optionSettings
}) => {
  const errorMessage = (touched && error) || helperText
  const classes = useStyles({ center })

  const handleChange = (_e, newValue): void => {
    if (!enforceValue) onChange(newValue)

    if (enforceValue && newValue !== null) {
      onChange(newValue)
    }
  }

  const { options, loading: isLoading } = useOptions({
    ...optionSettings,
    key: optionSettings?.key || name,
    options: customOptions,
    value
  })

  const onClickHandler = () => {
    if (value.length === options.length) {
      onChange([])
    } else {
      const allItems = options.map((option) => option.value)

      onChange(allItems)
    }
  }

  return (
    <FormControl
      variant="standard"
      classes={{
        root: classes.label
      }}
      label={label}
      helperText={errorMessage}
      error={!!errorMessage}
      size="medium"
      required={required}
    >
      <ToggleButtonGroup
        exclusive={!multiple}
        onChange={handleChange}
        classes={{
          grouped: classes.grouped
        }}
        className={classes.buttonGroup}
        value={value}
      >
        {!isLoading &&
          options.map((option) => {
            return (
              <ToggleButton
                size={size}
                style={{ position: 'relative' }}
                classes={{ selected: classes.selected }}
                key={option.value}
                value={option.value}
              >
                {processing && option.value === value ? (
                  <>
                    <span style={{ opacity: 0.2 }}>{option.label}</span>
                    <Loader size={20} zIndex={2} position="absolute" />
                  </>
                ) : (
                  option.label
                )}
              </ToggleButton>
            )
          })}
        {isLoading &&
          placeholderOptions.map((_v, index) => (
            <Skeleton key={index} height={58} variant="rect" width={150} />
          ))}
      </ToggleButtonGroup>
      {selectAll && (
        <Box mt={2} display="flex">
          <ExtendedButton
            color="secondary"
            onClick={() => {
              onClickHandler()
            }}
            fullWidth={false}
            mx="auto"
            disabled={!options?.length}
          >
            Select all
          </ExtendedButton>
        </Box>
      )}
    </FormControl>
  )
}

export default ToggleButtonField
